import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { Mail, Phone, MapPin, Users, DollarSign, Calendar, MessageSquare, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "closed"];

export default function AdminInquiries() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/inquiries?status=${filter}` : "/inquiries";
      const r = await adminApi.get(url);
      setList(r.data);
    } catch { toast.error("Failed to load inquiries"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      setList((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await adminApi.delete(`/inquiries/${id}`);
      toast.success("Inquiry deleted");
      setList((l) => l.filter((x) => x.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-inquiries-page">
      <div>
        <div className="eyebrow mb-3">Concierge Desk</div>
        <h1 className="font-serif text-4xl md:text-5xl">Inquiries.</h1>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {[{ id: "", label: "All" }, ...STATUSES.map((s) => ({ id: s, label: s }))].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id || "all"}`}
            className={`px-5 py-2.5 border text-[11px] uppercase tracking-[0.22em] transition-colors ${
              filter === f.id ? "bg-foreground text-background border-foreground" : "border-border text-foreground/65 hover:border-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-foreground/50 text-center py-20">Loading…</div>
      ) : list.length === 0 ? (
        <div className="border border-border p-16 text-center text-foreground/60">No inquiries match this filter.</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-inquiries-list">
          {list.map((inq) => (
            <li key={inq.id} className="border border-border bg-background">
              <button
                onClick={() => setExpanded(expanded === inq.id ? null : inq.id)}
                className="w-full p-5 lg:p-6 text-left flex flex-col lg:flex-row lg:items-center justify-between gap-3 hover:bg-secondary/40 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl">{inq.full_name}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 mt-1 truncate">{inq.destination} · {inq.budget_range} · {inq.travelers}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[11px] text-foreground/55 hidden sm:inline">{new Date(inq.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                  <span className={`px-3 py-1 border text-[10px] uppercase tracking-[0.22em] ${inq.status === "new" ? "border-accent text-accent" : inq.status === "contacted" ? "border-foreground/50 text-foreground/70" : "border-foreground/30 text-foreground/40"}`}>{inq.status}</span>
                  <ChevronDown size={14} className={`transition-transform ${expanded === inq.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {expanded === inq.id && (
                <div className="border-t border-border p-6 lg:p-8 bg-secondary/30 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <Info icon={Mail} label="Email" value={<a href={`mailto:${inq.email}`} className="hover:text-accent">{inq.email}</a>} />
                    <Info icon={Phone} label="Phone" value={inq.phone || "—"} />
                    <Info icon={MapPin} label="Destination" value={inq.destination} />
                    <Info icon={Users} label="Travelers" value={inq.travelers} />
                    <Info icon={DollarSign} label="Budget" value={inq.budget_range} />
                    <Info icon={Calendar} label="Dates" value={inq.travel_dates || "—"} />
                  </div>
                  {inq.preferences && (
                    <div className="pt-4 border-t border-border flex items-start gap-3 text-sm text-foreground/75">
                      <MessageSquare size={13} strokeWidth={1.25} className="text-accent mt-1 shrink-0" />
                      <span className="italic">{inq.preferences}</span>
                    </div>
                  )}
                  <div className="pt-4 border-t border-border flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(inq.id, s)}
                          disabled={inq.status === s}
                          className={`px-4 py-2 border text-[10px] uppercase tracking-[0.22em] transition-colors disabled:opacity-40 ${
                            inq.status === s ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
                          }`}
                        >
                          Mark {s}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => remove(inq.id)} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-destructive hover:opacity-70">
                      <Trash2 size={13} strokeWidth={1.25} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={13} strokeWidth={1.25} className="text-accent mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/55">{label}</div>
        <div className="mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
